import { useState } from 'react';

import { H2, H5 } from '@common/headline';
import {
  FAQProps,
  FAQsGroupScroll,
  FAQsGroupSelector,
  FAQsList,
} from './FAQsSection';
import { AboutTheProgramme } from './FAQsMedicallySupported';

const FAQs: Record<string, FAQProps[]> = {
  'About the medication': [
    {
      question: 'What is Mounjaro?',
      answer: (
        <span>
          Mounjaro is a dual-action weight-loss injection manufactured by the
          pharmaceutical company, Eli Lilly. The active ingredient in Mounjaro
          is tirzepatide.
          <br />
          <br />
          Tirzepatide is known as a GLP-1 and GIP receptor agonist.
        </span>
      ),
    },
    {
      question: 'How does Mounjaro work?',
      answer: (
        <span>
          Mounjaro works by mimicking two hormones in the body (GLP-1 and GIP)
          that interact with the brain{"'"}s appetite control centre, the
          hypothalamus, to lower food-seeking behaviours and hunger.
          <br />
          <br />
          By lowering hunger, we{"'"}re able to eat less and lose weight. You
          can{' '}
          <a
            className="font-medium text-blue"
            href="https://www.secondnature.io/guides/lifestyle/glp-1-receptor-agonists"
            rel="noreferrer"
            target="_blank"
          >
            read our full guide
          </a>{' '}
          for a more detailed analysis of how GLP-1 medications like Mounjaro
          work.
          <br />
          <br />
          Mounjaro has additional mechanisms which support the management of
          blood sugar and fat storage, improving overall health.
        </span>
      ),
    },
    {
      question: 'How much weight can you lose with Mounjaro?',
      answer: (
        <span>
          Data from{' '}
          <a
            className="font-medium text-blue"
            href="https://jamanetwork.com/journals/jama/fullarticle/2812936"
            rel="noreferrer"
            target="_blank"
          >
            randomised controlled trials
          </a>{' '}
          suggests that you can lose up to 25% of your body weight on Mounjaro
          after one year. If you weighed 100kg, that would mean losing 25kg.
        </span>
      ),
    },
    {
      question: 'How long will it take to lose weight on Mounjaro?',
      answer: (
        <span>
          According to{' '}
          <a
            className="font-medium text-blue"
            href="https://www.nejm.org/doi/10.1056/NEJMoa2107519?"
            rel="noreferrer"
            target="_blank"
          >
            randomised controlled trials
          </a>
          , you can start losing weight immediately after taking Mounjaro. After
          four weeks, the average weight loss is around 2% of body weight; after
          eight weeks, it{"'"}s over 4%.
          <br />
          <br />
          Interestingly, the initial weight loss is no different from other
          interventions to support weight loss, like diet and exercise without
          Mounjaro.
          <br />
          <br />
          However, Mounjaro is a long-acting medication, and the real impact is
          seen beyond three months.
        </span>
      ),
    },
    {
      question: 'How much does Mounjaro cost?',
      answer: (
        <span>
          You{"'"}ll pay £129 for the first month of your Mounjaro weight-loss
          programme. The amount you pay after this depends on the dose of
          medication you{"'"}re on. Please note prices include 1-1 support from
          a registered nutritionist or dietitian, as well as our our
          award-winning lifestyle change programme that will help you adopt
          healthy habits so you can keep the weight off for good.
        </span>
      ),
    },
    {
      question: 'Is Mounjaro the same as Wegovy?',
      answer: (
        <span>
          Mounjaro and Wegovy aren{"'"}t the same. The active ingredient in
          Mounjaro is tirzepatide, and semaglutide in Wegovy. However, they do
          share some similarities.
          <br />
          <br />
          Mounjaro and Wegovy are GLP-1 receptor agonists, weight-loss
          injections that reduce appetite to support weight loss and improve
          blood glucose control through various mechanisms.
          <br />
          <br />
          GLP-1 is a hormone that lowers hunger and food-seeking behaviours by
          communicating with the brain{"'"}s appetite control centre, the
          hypothalamus. Mounjaro and Wegovy mimic the actions of GLP-1.
          <br />
          <br />
          Mounjaro, in addition to being a GLP-1 receptor agonist, also mimics a
          hormone called GIP. GIP is a hormone in the body that supports blood
          glucose levels and helps our fat cells store more excess energy we
          might consume in our diets.
          <br />
          <br />
          The main difference between Mounjaro and Wegovy is that Mounjaro
          mimics two hormones while Wegovy only mimics one.
          <br />
          <br />
          Both medications have similar dosing schedules and are administered
          once a week. The main difference is the amount of medication used. The
          volume of the doses with Mounjaro is larger than with Wegovy.
        </span>
      ),
    },
    {
      question: 'Do you lose more weight on Wegovy or Mounjaro?',
      answer: (
        <span>
          Based on data from{' '}
          <a
            className="font-medium text-blue"
            href="https://jamanetwork.com/journals/jama/fullarticle/2812936"
            rel="noreferrer"
            target="_blank"
          >
            randomised controlled trials
          </a>
          , Mounjaro appears to lead to more weight loss than Wegovy.
          <br />
          <br />
          Research shows Mounjaro supports average weight loss of around 25%
          after one year and 16% on Wegovy.
          <br />
          <br />
          However, responses to medications vary between individuals, and it
          {"'"}s not certain which medication will support more weight loss as
          it{"'"}s also down to lifestyle changes such as diet and exercise.
        </span>
      ),
    },
    {
      question: 'Can you get Mounjaro on the NHS?',
      answer: (
        <span>
          Mounjaro is approved to be used in the NHS in the future. However, it
          has not been confirmed when the NHS will start prescribing Mounjaro.
          <br />
          <br />
          It{"'"}s likely you{"'"}ll need to be part of a tier 3 weight
          management service and meet some of the other eligibility criteria to
          get a prescription for Mounjaro on the NHS.
        </span>
      ),
    },
    {
      question: "What's the best diet with Mounjaro?",
      answer: (
        <span>
          The best diet to eat while on Mounjaro is one based primarily on whole
          foods that limit the consumption of ultra-processed foods rich in
          refined carbohydrates and sugar.
          <br />
          <br />
          On Second Nature, you{"'"}ll have the support of a registered
          dietitian or nutritionist to help you make sustainable improvements to
          your eating habits.
        </span>
      ),
    },
    {
      question: 'Is Mounjaro safe?',
      answer: (
        <span>
          Mounjaro and other GLP-1 medications are generally safe for most
          individuals. In a{' '}
          <a
            className="font-medium text-blue"
            href="https://jamanetwork.com/journals/jama/fullarticle/2812936"
            rel="noreferrer"
            target="_blank"
          >
            recent trial
          </a>
          , only 3% of participants out of 330 on Mounjaro experienced serious
          adverse events.
          <br />
          <br />
          Serious adverse events are those that hinder the individual{"'"}s
          quality of life and often lead to the discontinuation of the
          medication.
        </span>
      ),
    },
    {
      question: 'Does Mounjaro have side effects?',
      answer: (
        <span>
          As with all medications, Mounjaro comes with some potential side
          effects, and everyone will always respond differently.
          <br />
          <br />
          These should be mild for most people and tend to resolve over time as
          your body adjusts to the medication and dose.
          <br />
          <br />
          The most common side effects of Mounjaro are those related to the GI
          tract, such as nausea, diarrhoea, and constipation.
          <br />
          <br />
          It{"'"}s also common for some individuals to experience fatigue and
          lethargy on GLP-1 medications.
          <br />
          <br />
          However, these side effects are typically transient and will ease as
          you adjust to the medication.
          <br />
          <br />
          It can be hard at times to cope with these side effects, but we{"'"}re
          here to support you through your journey and will guide you every step
          of the way.
        </span>
      ),
    },
    {
      question: 'How do I get a Mounjaro prescription with Second Nature?',
      answer: (
        <span>
          You{"'"}ll need to complete a consultation form that will be reviewed
          by the clinical team. If you meet our eligibility criteria, you{"'"}ll
          receive Mounjaro in the post and start your Second Nature journey.
        </span>
      ),
    },
    {
      question: 'How do I take Mounjaro?',
      answer: (
        <span>
          You{"'"}ll inject the medication underneath the skin using an
          injection pen (a tiny needle - like a pinprick). You should inject
          into abdominal fat, thigh, or upper arm - ideally, you should rotate
          between these locations.
          <br />
          <br />
          This is a painless and quick process, and we{"'"}ll guide you through
          how to do this! Regardless of the dose, you{"'"}ll only inject
          Mounjaro once per week.
          <br />
          <br />
          Injections should be administered on the same day each week. These can
          be done at any time of day, with or without food.
        </span>
      ),
    },
    {
      question: 'What is the dosing schedule of Mounjaro?',
      answer: (
        <span>
          Mounjaro treatment starts with a 2.5 mg dose once a week. This dose is
          then increased by 2.5 mg every month until you reach the highest
          maintenance dose of 15 mg.
          <br />
          <br />
          However, if you respond well to lower doses of the medication and
          prefer to continue with a dose of 5 mg, for example, the clinical team
          can assist you with this.
        </span>
      ),
    },
    {
      question: 'What if I miss a dose of Mounjaro?',
      answer: (
        <span>
          If you forget to take a dose, you should inject as soon as you
          remember, as long as it{"'"}s within five days of the missed dose. You
          can then take your next dose as planned.
          <br />
          <br />
          For example, if you usually inject on a Monday, you have up to Friday
          to take your missed dose.
          <br />
          <br />
          If more than five days have passed since you forgot to take a dose,
          skip that injection and wait for the next injection day to come
          around.
          <br />
          <br />
          If you miss two consecutive weeks of doses, you should reach out to
          your health coach before restarting.
        </span>
      ),
    },
    {
      question: 'Can I be on other medications at the same time?',
      answer: (
        <span>
          There are some known drugs that interfere with tirzepartide. During
          your consultation, we{"'"}ll ask you to confirm all medications you
          {"'"}re currently taking. We{"'"}ll then advise on whether you{"'"}re
          eligible to take Mounjaro whilst on other medications.
        </span>
      ),
    },
    {
      question: 'How do I store the medication?',
      answer: (
        <span>
          The injection pens should be stored in a fridge. Don{"'"}t freeze
          them.
        </span>
      ),
    },
    {
      question: 'Can I travel with my injection pens?',
      answer: (
        <span>
          Yes - absolutely! You can travel through airports with your
          medication. You need to keep it in the box with the dispensing label
          clearly visible.
          <br />
          <br />
          This is so that airport security can verify that the medicine has been
          dispensed by a registered pharmacy in your name.
          <br />
          <br />
          If you have any issues, please contact{' '}
          <a
            className="font-medium text-blue"
            href="mailto:medication@secondnature.io"
            rel="noreferrer"
            target="_blank"
          >
            medication@secondnature.io
          </a>
          , and they{"'"}ll be able to support you.
        </span>
      ),
    },
  ],
  'About the programme': AboutTheProgramme,
};

export const MounjaroFAQsMedicallySupported = (): JSX.Element => {
  const FAQCategories = Object.keys(FAQs);
  const [activeGroup, setActiveGroup] = useState(FAQCategories[0]);

  const selectedCategory = FAQs[activeGroup];

  return (
    <div className="bg-mono-white pb-20 pt-[60px] lg:pb-28 lg:pt-32">
      <div className="flex w-full flex-col items-center">
        <div className="text-center text-blue">
          <H2>Got questions?</H2>
          <div className="mt-4">
            <H5>Great. We{"'"}ve got answers!</H5>
          </div>
        </div>
      </div>
      <div className="mt-[52px] lg:hidden">
        <div className="bg-purple-200 text-blue-400">
          <FAQsGroupScroll
            activeGroup={activeGroup}
            groupNames={FAQCategories}
            setActiveGroup={setActiveGroup}
          />
        </div>
      </div>
      <div className="flex w-full justify-center px-3 lg:px-[60px]">
        <div className="flex w-full justify-center lg:w-[1124px] lg:justify-between">
          <div className="mt-28 hidden lg:block">
            <div className="w-[248px]">
              <FAQsGroupSelector
                activeGroup={activeGroup}
                groupNames={FAQCategories}
                setActiveGroup={setActiveGroup}
              />
            </div>
          </div>
          <div className="mt-12 w-[351px] bg-mono-white text-blue md:w-[540px] lg:ml-28 lg:mt-32 lg:w-full">
            <FAQsList FAQs={selectedCategory} />
          </div>
        </div>
      </div>
    </div>
  );
};
